import React, { ReactNode, useEffect, useReducer } from 'react';
import context from 'react-bootstrap/esm/AccordionContext';
import { PolygonUtils } from './components/PolygonUtils';
import { useSettings } from './hook';
import { getParamFromUrl, parseZoomLevel, redirectToErrorPage } from './utils/utils';

interface WebWidgetContextProps {
  children: ReactNode | ReactNode[]
}

interface ContextProps {
  orgId: string,
  stream?: string,
  lang: string,
  state: any,
  autoExpand: boolean,
  actionDispatch: (type: WEB_WIDGET_ACTIONS, value: any) => void
}

export enum VIEW_NAMES {
  LOADING_VIEW,
  NEW_SETTINGS_VIEW,
  OVERRIDE_SETTINGS_VIEW,
  PUBLIC_ORG_TIP_VIEW,
  SETTINGS_DETAIL_VIEW
}

export enum WEB_WIDGET_ACTIONS {
  LOADING_SETTINGS = 'LOADING_SETTINGS',
  FETCH_ALERT_DETAIL = 'FETCH_ALERT_DETAIL',
  FETCH_ALERT_DETAIL_DONE = 'FETCH_ALERT_DETAIL_DONE',
  GOOGLE_MAP_LOADED = 'GOOGLE_MAP_LOADED',
  SETTINGS_LOADED = 'SETTINGS_LOADED',
  SELECT_ALERT = 'SELECT_ALERT',
  SHOW_NO_SHAPE_MSG = 'SHOW_NO_SHAPE_MSG',
  HIDE_NO_SHAPE_MSG = 'HIDE_NO_SHAPE_MSG'
}

function reducer(state: any, action: any) {
  const { type, value } = action
  console.log('action type:', type)
  console.log('action value:', value)

  switch (type) {
    case WEB_WIDGET_ACTIONS.LOADING_SETTINGS:
      return { ...state, loadingSettings: true };
    case WEB_WIDGET_ACTIONS.FETCH_ALERT_DETAIL:
      return { ...state, loadingAlertDetail: true };
    case WEB_WIDGET_ACTIONS.FETCH_ALERT_DETAIL_DONE:
      return { ...state, loadingAlertDetail: false };
    case WEB_WIDGET_ACTIONS.SETTINGS_LOADED:
      const { googleMapAPIKey, mapCenter } = value;
      return { ...state, googleMapAPIKey, mapCenter, loadingSettings: false };
    case WEB_WIDGET_ACTIONS.GOOGLE_MAP_LOADED:
      const { google, map } = value;
      const polygonUtils = PolygonUtils(google, map);
      return { ...state, google, map, polygonUtils };
    case WEB_WIDGET_ACTIONS.SELECT_ALERT:
      return { ...state, selectedAlert: value };
    case WEB_WIDGET_ACTIONS.SHOW_NO_SHAPE_MSG:
      return { ...state, showNoShapeMessage: true };
    case WEB_WIDGET_ACTIONS.HIDE_NO_SHAPE_MSG:
      return { ...state, showNoShapeMessage: false };
    default:
      new Error();
  }
}

export const WebWidgetContext = React.createContext<ContextProps>({
  orgId: '',
  stream: '',
  lang: '',
  autoExpand: true,
  state: null,
  actionDispatch: () => { }
})

export const WebWidgetProvider: React.FC<WebWidgetContextProps> = ({ children }) => {

  const orgId = getParamFromUrl('o');
  const stream = getParamFromUrl('s')
  const zoom = getParamFromUrl('zoom');
  const lat = getParamFromUrl('lat');
  const lon = getParamFromUrl('lon');
  const lang = getParamFromUrl('lang') || 'en_US';
  const autoExpand = (getParamFromUrl('expand') || '').toLowerCase() !== 'false';

  if (!orgId) {
    redirectToErrorPage();
  }

  const [state, dispatch] = useReducer(reducer, {
    google: null,
    map: null,
    loadingSettings: true,
    loadingAlertDetail: false,
    showNoShapeMessage: false,
    googleMapKey: '',
    mapCenter: {
      zoom: parseInt(zoom),
      lat: parseFloat(lat),
      lon: parseFloat(lon)
    },
    selectedAlert: null,
    polygonUtils: null,
  });

  const [, , , , fetchMetadataAndSettings] = useSettings(orgId);

  const contextValues = {
    orgId: orgId,
    stream: stream,
    lang: lang,
    autoExpand: autoExpand,
    state: state,
    actionDispatch: (type: WEB_WIDGET_ACTIONS, value?: any) => dispatch({ type, value })
  };

  useEffect(() => {
    console.log('fetch settings')
    contextValues.actionDispatch(WEB_WIDGET_ACTIONS.LOADING_SETTINGS, true);
    fetchMetadataAndSettings().then((result) => {
      const { data } = result;
      const { mapCenter, googleMapAPIKey } = data;
      const center = {
        zoom: (!isNaN(contextValues.state.mapCenter.zoom)) ? contextValues.state.mapCenter.zoom : parseZoomLevel(mapCenter.zoom),
        lat: (!isNaN(contextValues.state.mapCenter.lat)) ? contextValues.state.mapCenter.lat : mapCenter.lat,
        lon: (!isNaN(contextValues.state.mapCenter.lon)) ? contextValues.state.mapCenter.lon : mapCenter.lon,
      }
      console.log('center:', center)
      contextValues.actionDispatch(WEB_WIDGET_ACTIONS.SETTINGS_LOADED, { googleMapAPIKey, mapCenter: center });
    }).catch(() => {
      redirectToErrorPage();
    });
  }, []);

  return (
    <WebWidgetContext.Provider
      value={contextValues}>
      {children}
    </WebWidgetContext.Provider>
  );
}
